import { useState } from 'react';
import { Link } from 'react-router';
import { formatDataLocal, formatPhoneBR, nomeAeroporto, whatsappLink } from '@agencia/shared';
import { CiaSelo, IconeAviao } from '@/components/CiaSelo';
import { Alert } from '@/components/ui';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { cancelado, mensagemCliente, nomeStatus, type VooProximo } from '@/lib/voos';

function haQuanto(iso: string): string {
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (min < 2) return 'agora há pouco';
  if (min < 60) return `há ${min} min`;
  const h = Math.round(min / 60);
  return h < 24 ? `há ${h} h` : `há ${Math.round(h / 24)} dia(s)`;
}

/** Um voo da lista de próximos voos (no estilo de cartão de embarque). */
export function VooCard({ v, monitorLigado, mostrarConsultor, onChanged }: { v: VooProximo; monitorLigado: boolean; mostrarConsultor: boolean; onChanged: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [copiado, setCopiado] = useState(false);
  const pendente = Boolean(v.alteracao && !v.alteracaoVistaEm);
  const cancel = cancelado(v);

  async function acao(nome: string, fn: () => Promise<unknown>) {
    setBusy(nome);
    setErro(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setErro(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  async function copiarLoc() {
    try {
      await navigator.clipboard.writeText(v.sobrenome ? `${v.localizador} ${v.sobrenome}` : v.localizador);
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 2000);
    } catch {
      /* sem permissão: o localizador já está visível */
    }
  }

  const status = nomeStatus(v.statusVoo);
  return (
    <article className={`voo ${pendente ? 'voo--alerta' : ''} ${v.checkinFeitoEm ? 'voo--ok' : ''}`}>
      <header className="voo__top">
        <CiaSelo companhia={v.companhia} nome={v.companhiaNome} />
        <div className="voo__id">
          <span className="voo__rotulo">Localizador</span>
          <strong className="voo__loc">{v.localizador}</strong>
          <span className="muted small">
            Voo {v.companhia} {v.numeroVoo}
            {v.trechosTotal > 1 ? ` · trecho ${v.trechoN} de ${v.trechosTotal}` : ''}
          </span>
        </div>
        <div className="voo__tags">
          <span className="voo__pax">{v.passageiros.length === 1 ? '1 passageiro' : `${v.passageiros.length || '?'} passageiros`}</span>
          {v.checkinFeitoEm && <span className="pill pill--ok">Check-in feito</span>}
        </div>
      </header>

      <div className={`voo__rota ${cancel ? 'is-cancel' : ''}`}>
        <div>
          <strong className="voo__iata">{v.origem}</strong>
          <span className="voo__hora">{v.partida.slice(11, 16)}</span>
          <span className="voo__dia">{formatDataLocal(v.partida)}</span>
          <span className="voo__cidade">{nomeAeroporto(v.origem)}</span>
        </div>
        <span className="voo__seta" aria-hidden="true">
          <IconeAviao />
        </span>
        <div className="voo__dest">
          <strong className="voo__iata">{v.destino}</strong>
          <span className="voo__hora">{v.chegada ? v.chegada.slice(11, 16) : '—'}</span>
          <span className="voo__dia">{v.chegada ? formatDataLocal(v.chegada) : ''}</span>
          <span className="voo__cidade">{nomeAeroporto(v.destino)}</span>
        </div>
      </div>

      {v.reservaConferida && v.reservaConferida.status !== 'OK' && (
        <p className="voo__novo voo__novo--bad">
          Conferência: reserva <strong>{v.reservaConferida.status === 'CANCELADA' ? 'CANCELADA' : v.reservaConferida.status === 'ALTERADA' ? 'alterada' : 'não encontrada'}</strong> no site da companhia
        </p>
      )}

      {v.partidaPrevista && !cancel && (
        <p className="voo__novo">
          Novo horário informado: saída {formatDataLocal(v.partidaPrevista)} às <strong>{v.partidaPrevista.slice(11, 16)}</strong>
        </p>
      )}

      {v.alteracao && (
        <div className={`voo__alteracao ${pendente ? '' : 'is-vista'}`} role={pendente ? 'alert' : undefined}>
          <strong>{pendente ? 'Alteração no voo' : 'Alteração vista'}</strong>
          <span>{v.alteracao}</span>
          {pendente && (
            <div className="row tight">
              {v.cliente.telefone && (
                <a className="btn btn--sm btn--primary" href={whatsappLink(v.cliente.telefone, mensagemCliente(v))} target="_blank" rel="noreferrer">
                  Avisar o cliente no WhatsApp
                </a>
              )}
              <button type="button" className="btn btn--sm btn--secondary" disabled={busy !== null} onClick={() => void acao('visto', () => api.post(`/crm/trechos/${v.id}/visto`))}>
                {busy === 'visto' ? 'Salvando…' : 'Já resolvi / avisei'}
              </button>
            </div>
          )}
        </div>
      )}

      <div className="voo__quem small">
        <Link to={`/clientes/${v.cliente.id}`}>
          <strong>{v.cliente.nome}</strong>
        </Link>
        {v.cliente.telefone && <span className="muted"> · {formatPhoneBR(v.cliente.telefone)}</span>}
        {mostrarConsultor && <span className="muted"> · {v.consultor?.nome ?? 'sem consultor'}</span>}
        {v.venda && (
          <>
            {' · '}
            <Link to={`/vendas/${v.venda.id}`}>venda #{v.venda.numero ?? '—'}</Link>
          </>
        )}
        {v.passageiros.length > 0 && <div className="muted voo__nomes">{v.passageiros.join(' · ')}</div>}
      </div>

      {erro && <Alert>{erro}</Alert>}

      <div className="voo__acoes">
        {v.checkinUrl ? (
          <a className="btn btn--checkin" href={v.checkinUrl} target="_blank" rel="noreferrer" onClick={() => void copiarLoc()}>
            Fazer check-in
          </a>
        ) : (
          <span className="muted small">Check-in pelo site da companhia</span>
        )}
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => void copiarLoc()} title="Copia o localizador e o sobrenome">
          {copiado ? 'Copiado!' : `Copiar ${v.localizador}${v.sobrenome ? ` ${v.sobrenome}` : ''}`}
        </button>
        {v.cliente.telefone && !pendente && (
          <a className="btn btn--ghost btn--sm" href={whatsappLink(v.cliente.telefone, mensagemCliente(v))} target="_blank" rel="noreferrer">
            Lembrete no WhatsApp
          </a>
        )}
        {v.bilheteId && (
          <a className="btn btn--ghost btn--sm" href={`/api/crm/files/${v.bilheteId}`}>
            Bilhete (PDF)
          </a>
        )}
      </div>

      <footer className="voo__rodape small">
        <label className="switch">
          <input
            type="checkbox"
            checked={Boolean(v.checkinFeitoEm)}
            disabled={busy !== null}
            onChange={(e) => void acao('checkin', () => api.post(`/crm/trechos/${v.id}/checkin`, { feito: e.target.checked }))}
          />
          <span>{v.checkinFeitoEm ? `Check-in feito${v.checkinPor ? ` por ${v.checkinPor.split(' ')[0]}` : ''}` : 'Check-in realizado'}</span>
        </label>
        {monitorLigado && (
          <span className="muted">
            {status ? `Status: ${status}` : 'Ainda não consultado'}
            {v.consultadoEm ? ` · ${haQuanto(v.consultadoEm)}` : ''}{' '}
            <button type="button" className="linklike" disabled={busy !== null} onClick={() => void acao('consultar', () => api.post(`/crm/trechos/${v.id}/consultar`))}>
              {busy === 'consultar' ? 'consultando…' : 'consultar agora'}
            </button>
          </span>
        )}
      </footer>
    </article>
  );
}
